// src/SallerAdmin/pages/Profile/BusinessProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { 
    Building2, MapPin, Globe, Phone, Mail, FileText, 
    Save, Loader2, ChevronDown, CheckCircle, AlertCircle, X,
    Briefcase
} from 'lucide-react';

// --- TOAST NOTIFICATION COMPONENT ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={20} className="text-green-600"/> : <AlertCircle size={20} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

// --- HELPER COMPONENTS ---
const FormInput = ({ label, name, value, onChange, placeholder, required = false, type = "text", disabled = false, icon: Icon, maxLength }: any) => (
    <div className="w-full">
        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative group">
            {Icon && <div className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-[#00635e] transition-colors"><Icon size={18} /></div>}
            <input
                type={type}
                name={name}
                value={value || ''}
                onChange={onChange}
                disabled={disabled}
                placeholder={placeholder}
                maxLength={maxLength}
                className={`w-full border border-gray-300 rounded-lg py-2.5 ${Icon ? 'pl-10' : 'pl-3'} pr-3 text-sm focus:ring-2 focus:ring-[#00635e] focus:border-[#00635e] outline-none transition-all shadow-sm ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}`}
            />
        </div>
    </div>
);

const SectionHeader = ({ icon: Icon, title, isOpen, toggle, subtitle }: any) => (
    <button 
        type="button"
        className={`w-full flex justify-between items-center px-5 py-4 transition-colors ${isOpen ? 'bg-teal-50/50 border-b border-teal-100' : 'bg-white hover:bg-gray-50'}`}
        onClick={toggle}
    >
        <div className="flex items-center text-left">
            <div className={`p-2 rounded-lg mr-3 ${isOpen ? 'bg-[#00635e] text-white' : 'bg-gray-100 text-gray-500'}`}>
                <Icon size={20} />
            </div>
            <div>
                <h3 className={`font-bold text-sm md:text-base ${isOpen ? 'text-[#00635e]' : 'text-gray-800'}`}>{title}</h3>
                {subtitle && <p className="text-xs text-gray-500 mt-0.5 hidden md:block">{subtitle}</p>}
            </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#00635e]' : ''}`} />
    </button>
);

const BusinessProfilePage: React.FC = () => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    
    // Toast State
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Section Toggles
    const [sections, setSections] = useState({ company: true, address: true, tax: true });
    
    // Form Data State
    const [formData, setFormData] = useState({
        companyName: '',
        companyEmail: '',
        officeContact: '',
        website: '',
        gstNumber: '',
        panNumber: '',
        aadhaarNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'India'
    });

    const getToken = () => localStorage.getItem("token");

    // Helper to show toast
    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // --- 1. Fetch Existing Data ---
    useEffect(() => {
        if (user?.id) {
            fetchBusinessProfile();
        }
    }, [user]);

    const fetchBusinessProfile = async () => {
        try {
            // Mock Data for Demo (Replace with actual API call)
            // setFormData({ ...formData, companyName: "TradyFrog Pvt Ltd", city: "New Delhi" });
            
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/business`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data && data.companyName) {
                    setFormData(prev => ({ ...prev, ...data }));
                } else {
                    setFormData(prev => ({
                        ...prev,
                        companyName: user?.companyName || '',
                        officeContact: user?.mobile || '',
                        companyEmail: user?.email || ''
                    }));
                }
            }
        } catch (e) { console.error("Fetch Error", e); }
        finally { setPageLoading(false); }
    };

    // --- 2. Handle Change ---
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Auto-Uppercase for PAN and GST
        if (name === 'panNumber' || name === 'gstNumber') {
            setFormData({ ...formData, [name]: value.toUpperCase() });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    // --- 3. Handle Submit ---
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/business`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                showToast("Business Profile Saved Successfully!");
                fetchBusinessProfile(); 
            } else {
                showToast("Failed to save profile. Please check inputs.", "error");
            }
        } catch (e) {
            showToast("Server Error. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };

    // --- Auto-Fill Pincode Logic ---
    const handlePincodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
        setFormData(prev => ({ ...prev, postalCode: val }));
        
        if (val.length === 6) {
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
                const data = await res.json();
                if (data[0].Status === "Success") {
                    const place = data[0].PostOffice[0];
                    setFormData(prev => ({ ...prev, city: place.District, state: place.State, country: 'India' }));
                    showToast(`Location detected: ${place.District}, ${place.State}`);
                }
            } catch (e) { console.error(e); }
        }
    };

    if (pageLoading) return <div className="p-10 text-center text-gray-500">Loading Business Profile...</div>;

    return (
        <div className=" bg-gray-50/50 min-h-screen font-sans relative">
            
            {/* --- TOAST --- */}
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            {/* --- HEADER --- */}
            <div className="max-w-7xl mx-auto mb-8">
                <h1 className="text-xl md:text-2xl font-black text-[#00635e] border-b border-gray-200 pb-4 flex items-center">
                    <Briefcase className="mr-3 text-red-600" size={28} />
                    Business Profile <span className="ml-2 text-sm md:text-base font-medium text-gray-400 font-sans hidden sm:inline-block">(Identity & Tax Details)</span>
                </h1>
                <p className="mt-2 text-sm text-gray-500">Manage your company information, registered address, and tax identification numbers here.</p>
            </div>

            <form onSubmit={handleSubmit} className="max-w-7xl mx-auto space-y-6 pb-20 ">

                {/* --- SECTION 1: COMPANY DETAILS --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md ">
                    <SectionHeader 
                        icon={Building2} 
                        title="Company Identity & Contact" 
                        subtitle="Basic details about your business entity."
                        isOpen={sections.company} 
                        toggle={() => setSections({ ...sections, company: !sections.company })} 
                    />
                    
                    {sections.company && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 fade-in duration-200">
                            <FormInput name="companyName" label="Company Name" value={formData.companyName} onChange={handleChange} required placeholder="e.g. TradyFrog Pvt Ltd" icon={Building2} />
                            <FormInput name="website" label="Company Website" value={formData.website} onChange={handleChange} placeholder="https://www.example.com" icon={Globe} />
                            
                            <FormInput name="companyEmail" label="Company Email" type="email" value={formData.companyEmail} onChange={handleChange} required placeholder="official@company.com" icon={Mail} />
                            <FormInput name="officeContact" label="Office Contact Number" value={formData.officeContact} onChange={handleChange} required placeholder="+91 9999999999" icon={Phone} maxLength={10} />
                        </div>
                    )}
                </div>

                {/* --- SECTION 2: COMPANY ADDRESS --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md">
                    <SectionHeader 
                        icon={MapPin} 
                        title="Registered Office Address" 
                        subtitle="Location details for invoices and shipping."
                        isOpen={sections.address} 
                        toggle={() => setSections({ ...sections, address: !sections.address })} 
                    />
                    
                    {sections.address && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 fade-in duration-200">
                             <div className="md:col-span-2">
                                <FormInput name="addressLine1" label="Address Line 1" value={formData.addressLine1} onChange={handleChange} required placeholder="Building No, Street, Area" icon={MapPin} />
                            </div>
                            <div className="md:col-span-2">
                                <FormInput name="addressLine2" label="Address Line 2 (Optional)" value={formData.addressLine2} onChange={handleChange} placeholder="Landmark, Floor, etc." />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Pincode <span className="text-red-500">*</span></label>
                                <input 
                                    type="text" 
                                    value={formData.postalCode} 
                                    onChange={handlePincodeChange}
                                    maxLength={6}
                                    className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-[#00635e] focus:border-[#00635e] outline-none shadow-sm transition-all"
                                    placeholder="Enter 6 digit pincode"
                                    required
                                />
                            </div>

                            <FormInput name="city" label="City" value={formData.city} onChange={handleChange} required placeholder="Auto-detected" disabled />
                            <FormInput name="state" label="State" value={formData.state} onChange={handleChange} required placeholder="Auto-detected" disabled />
                            <FormInput name="country" label="Country" value={formData.country} onChange={handleChange} disabled />
                        </div>
                    )}
                </div>

                {/* --- SECTION 3: REGISTRATION & TAXATION --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md">
                    <SectionHeader 
                        icon={FileText} 
                        title="Registration & Taxation (KYC)" 
                        subtitle="GSTIN, PAN and other legal info."
                        isOpen={sections.tax} 
                        toggle={() => setSections({ ...sections, tax: !sections.tax })} 
                    />
                    
                    {sections.tax && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 fade-in duration-200">
                            <FormInput name="gstNumber" label="GST Number" value={formData.gstNumber} onChange={handleChange} required placeholder="GSTIN (15 Digits)" maxLength={15} />
                            <FormInput name="panNumber" label="Company PAN" value={formData.panNumber} onChange={handleChange} required placeholder="Permanent Account Number" maxLength={10} />
                            
                            <div className="md:col-span-3">
                                <FormInput name="aadhaarNumber" label="Owner's Aadhaar Number" value={formData.aadhaarNumber} onChange={handleChange} required placeholder="12 Digit Aadhaar Number" maxLength={12} />
                                <p className="text-[10px] text-gray-400 mt-1 italic">*Required for verification purposes only.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- SUBMIT BUTTON --- */}
                <div className="flex justify-end pt-4 pb-10">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center justify-center px-8 py-3.5 bg-red-600 text-white font-bold rounded-xl shadow-lg hover:bg-red-700 transition-all active:scale-95 disabled:opacity-70 disabled:scale-100"
                    >
                        {loading ? <Loader2 className="animate-spin mr-2" size={20} /> : <Save className="mr-2" size={20} />}
                        {loading ? 'Saving Profile...' : 'Save Business Profile'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default BusinessProfilePage;